// Device-local, guest-safe conversation memory for the KA Intelligence beta.
//
// Rules:
//   • localStorage only. Nothing is sent to the server for persistence.
//   • Bounded message count and character budget.
//   • TTL: a stale conversation is dropped, not resumed.
//   • Versioned schema: an unknown/corrupt payload fails safe to "empty".
//   • No identity, no fingerprint, no cross-device linkage.

export const LOCAL_MEMORY_KEY = "ka.intelligence.conversation";
export const LOCAL_MEMORY_VERSION = 1;
export const LOCAL_MEMORY_TTL_MS = 24 * 60 * 60 * 1000;
export const LOCAL_MEMORY_MAX_MESSAGES = 20;
const MAX_TEXT = 8000;

export interface LocalMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
}

export interface LocalConversation {
  v: number;
  conversationId: string;
  updatedAt: number;
  messages: LocalMessage[];
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function defaultStorage(): StorageLike | null {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function boundMessages(messages: LocalMessage[]): LocalMessage[] {
  return messages
    .filter((m) => m && (m.role === "user" || m.role === "assistant") && typeof m.text === "string")
    .slice(-LOCAL_MEMORY_MAX_MESSAGES)
    .map((m) => ({ id: String(m.id).slice(0, 64), role: m.role, text: m.text.slice(0, MAX_TEXT) }));
}

/** Load the active local conversation, or null when absent/stale/corrupt. */
export function loadLocalConversation(
  storage: StorageLike | null = defaultStorage(),
  now = Date.now(),
): LocalConversation | null {
  if (!storage) return null;
  let raw: string | null;
  try {
    raw = storage.getItem(LOCAL_MEMORY_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearLocalConversation(storage);
    return null;
  }
  const c = parsed as Partial<LocalConversation>;
  if (
    !c ||
    typeof c !== "object" ||
    c.v !== LOCAL_MEMORY_VERSION ||
    typeof c.conversationId !== "string" ||
    typeof c.updatedAt !== "number" ||
    !Array.isArray(c.messages)
  ) {
    clearLocalConversation(storage);
    return null;
  }
  if (now - c.updatedAt > LOCAL_MEMORY_TTL_MS) {
    clearLocalConversation(storage);
    return null;
  }
  return {
    v: LOCAL_MEMORY_VERSION,
    conversationId: c.conversationId.slice(0, 64),
    updatedAt: c.updatedAt,
    messages: boundMessages(c.messages as LocalMessage[]),
  };
}

/** Persist the active conversation locally. No-ops when storage is unavailable. */
export function saveLocalConversation(
  conversation: { conversationId: string; messages: LocalMessage[] },
  storage: StorageLike | null = defaultStorage(),
  now = Date.now(),
): void {
  if (!storage) return;
  const messages = boundMessages(conversation.messages);
  if (messages.length === 0) {
    clearLocalConversation(storage);
    return;
  }
  const payload: LocalConversation = {
    v: LOCAL_MEMORY_VERSION,
    conversationId: conversation.conversationId.slice(0, 64),
    updatedAt: now,
    messages,
  };
  try {
    storage.setItem(LOCAL_MEMORY_KEY, JSON.stringify(payload));
  } catch {
    /* quota or private mode — local memory is best-effort only */
  }
}

/** Remove local messages AND local metadata. Nothing survives on the device. */
export function clearLocalConversation(storage: StorageLike | null = defaultStorage()): void {
  if (!storage) return;
  try {
    storage.removeItem(LOCAL_MEMORY_KEY);
  } catch {
    /* nothing else to do */
  }
}
